/* Shared cart checkout for every template. PDP Buy Now prepares its own cart and
 * keeps its separate pricing/readiness flow in custom.js. No heavy apps load here. */
(function () {
  var inProgress = false;

  function stopWithMessage(message) {
    // Console-only: keep the four failure gates distinguishable (BSS unavailable /
    // pricing not ready / did not navigate / cart empty) without a user-facing alert.
    console.error('[mt-cart-checkout] ' + message);
  }

  function nativeCheckout(codes) {
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = (window.routes && window.routes.cart_url) || '/cart';
    if (codes && codes.length) {
      var discount = document.createElement('input');
      discount.type = 'hidden';
      discount.name = 'discount';
      discount.value = codes.join(',');
      form.appendChild(discount);
    }
    var checkout = document.createElement('input');
    checkout.type = 'hidden';
    checkout.name = 'checkout';
    form.appendChild(checkout);
    document.body.appendChild(form);
    form.submit();
  }

  function codesFromCart(cart) {
    var codes = (cart.discount_codes || []).filter(function (entry) {
      return entry.applicable !== false && entry.code;
    }).map(function (entry) { return entry.code; });
    if (codes.length) return codes;
    return (cart.cart_level_discount_applications || []).filter(function (entry) {
      return entry.type === 'discount_code' && entry.title;
    }).map(function (entry) { return entry.title; });
  }

  function pricedMerchandiseReady(bss, cart) {
    var storage = bss.storage && bss.storage.cartStorage;
    var stored = storage && storage.getAll && storage.getAll();
    if (!stored || !cart || !cart.items || !cart.items.length) return false;
    var rows = stored instanceof Map ? stored : new Map(Object.entries(stored));
    if (rows.size !== cart.items.length) return false;
    var shippingId = document.querySelector('cart-drawer')?.getAttribute('data-shipping-id');
    var merchandise = 0;
    for (var item of cart.items) {
      var row = rows.get(item.key);
      if (!row) return false;
      if (shippingId && String(item.variant_id || item.id) === shippingId) continue;
      merchandise++;
      // BSS's checkout handler needs a resolved pricing entry, not merely an
      // appliedPL flag. Its pricing methods are the same ones used by checkout/create.
      var pl = row.appliedPL && bss.pl?.utils?.common?.getPlPricing(row);
      var cp = row.appliedCP && bss.cp?.utils?.common?.getCpPricing(row);
      var dc = row.appliedDC && bss.dc?.utils?.common?.getDcPricing(row);
      var qb = row.appliedQB && row.bssB2BQbActiveItem && bss.qb?.utils?.common;
      var bogo = row.appliedBogo && bss.bogo?.utils?.common;
      var ef = bss.ef?.status && bss.ef?.utils?.cart?.getEfPricingList?.().length;
      var shipping = bss.sr?.status;
      var otherRules = bss.te?.status || bss.on?.status;
      if (!pl && !cp && !dc && !qb && !bogo && !ef && !shipping && !otherRules) return false;
    }
    return merchandise > 0;
  }

  function awaitNavigation(ms) {
    return new Promise(function (resolve) {
      if (document.visibilityState === 'hidden') return resolve(true);
      var done = false;
      var timer;
      function finish(value) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        window.removeEventListener('pagehide', onHide);
        document.removeEventListener('visibilitychange', onVisibility);
        resolve(value);
      }
      function onHide() { finish(true); }
      function onVisibility() {
        if (document.visibilityState === 'hidden') finish(true);
      }
      window.addEventListener('pagehide', onHide);
      document.addEventListener('visibilitychange', onVisibility);
      timer = setTimeout(function () { finish(document.visibilityState === 'hidden'); }, ms);
    });
  }

  async function run(options) {
    if (inProgress) return;
    inProgress = true;
    try {
      var prepared = await options.prepare();
      var cart = prepared?.cart || await fetch('/cart.js').then(function (r) { return r.json(); });
      var codes = prepared?.codes || codesFromCart(cart);
      if (!cart.items?.length) {
        stopWithMessage('Cart is empty; checkout stopped');
        return;
      }
      if (document.documentElement.hasAttribute('data-mt-guest')) return nativeCheckout(codes);

      // A cart click, unlike PDP Buy Now, may be the first commerce intent.
      // Start BSS directly rather than waiting for the heavy tier's 450ms timer.
      if (typeof window.__mtActivateBssForCheckout === 'function') {
        await window.__mtActivateBssForCheckout();
      }
      var bss = window.BSS_B2B;
      var subject = bss?.observer?.cartSubject;
      if (!subject || typeof subject.notifyObserver !== 'function') {
        // Cold start: BSS is script-gated until first cart intent and the
        // activation poll above gives up after 1.5s — heavy BSS bundles can
        // initialize slower than that right after a first add-to-cart.
        // Checkout is the one moment where waiting beats a false
        // "pricing unconfirmed" alert, so keep polling before giving up.
        var bssWaitUntil = Date.now() + 6500;
        while (
          Date.now() < bssWaitUntil &&
          !(
            window.BSS_B2B &&
            window.BSS_B2B.observer &&
            window.BSS_B2B.observer.cartSubject &&
            typeof window.BSS_B2B.observer.cartSubject.notifyObserver === 'function'
          )
        ) {
          await new Promise(function (resolve) { setTimeout(resolve, 100); });
        }
        bss = window.BSS_B2B;
        subject = bss?.observer?.cartSubject;
      }
      if (!subject || typeof subject.notifyObserver !== 'function') {
        stopWithMessage('BSS unavailable; wholesale checkout stopped');
        return;
      }
      // Shipping-protection/cart mutations can replace BSS storage during prepare.
      // Resynchronise with the final Shopify cart before allowing BSS to decide
      // whether there is a pricing entry (its z flag).
      if (!pricedMerchandiseReady(bss, cart)) {
        var type = bss.enum?.CartTriggerTags?.CustomCartUpdate || 'CustomCartUpdate';
        await subject.notifyObserver('CartUpdate', 'CustomCartUpdate', {
          event: new CustomEvent('bss_b2b:' + type, { detail: { cart: cart } })
        });
        var until = Date.now() + 8000;
        var lastRefresh = Date.now();
        while (Date.now() < until && !pricedMerchandiseReady(bss, cart)) {
          // The shipping-protection auto-add can mutate the cart AFTER
          // prepare() captured its snapshot; BSS then holds more rows than
          // the snapshot and readiness never passes. Re-read the live cart
          // every 2s so the comparison cannot deadlock on stale state.
          if (Date.now() - lastRefresh > 2000) {
            lastRefresh = Date.now();
            try {
              cart = await fetch('/cart.js').then(function (r) { return r.json(); });
            } catch (e) { /* keep polling with the previous snapshot */ }
          }
          await new Promise(function (resolve) { setTimeout(resolve, 100); });
        }
      }
      if (!pricedMerchandiseReady(bss, cart)) {
        stopWithMessage('BSS pricing not ready; wholesale checkout stopped');
        return;
      }
      // BSS builds its checkout entries from cartStorage before requesting checkout/create.
      // An unconditional native fallback here would silently discard wholesale prices.
      var navigation = awaitNavigation(8000);
      await subject.notifyObserver('CartCheckout', 'ClickCheckout', {
        event: { preventDefault: function () {}, stopPropagation: function () {}, stopImmediatePropagation: function () {} }
      });
      if (!await navigation) {
        stopWithMessage('BSS checkout did not navigate; wholesale checkout stopped');
      }
    } catch (error) {
      console.error('[mt-cart-checkout] checkout stopped', error);
    } finally {
      inProgress = false;
    }
  }

  window.mtCartCheckout = { run: run, get busy() { return inProgress; } };
})();
